import React, { FC } from 'react';
import styles from './styles.module.css';
import { Button, Dropdown } from 'antd';
import { ExternalLink } from 'lucide-react';
import * as translations from '../../strings';
import { getLangKey } from 'appJS/models/Language';
import ProxyCompatibilityChecker from '../../../../../clients/design_assets/ProxyCompatibilityChecker';
import { useProjectSitesState } from '../../../../../../clients/providers/ProjectSites';
import { useOrganizationState } from '../../../../../../clients/providers/Organization';

type Props = {
  project: any;
  projectSites: {
    url: string;
    id: number;
  }[];
  container: HTMLDivElement;
  isMobileView: boolean;
  isReviewApp: boolean;
  isAdminUser: boolean;
  apiKey: string;
};

type ButtonProps = {
  project: any;
  projectSite: {
    url: string;
    id: number;
  };
  showAsLabel?: boolean;
  isMobileView: boolean;
};

const strings = translations[getLangKey()];

const OpenWebsiteButton: FC<ButtonProps> = ({
  project,
  projectSite,
  showAsLabel,
  isMobileView,
  ...restProps
}) => {
  const { proxyHost } = useProjectSitesState();
  const { hasProxyEnabledExperiment } = useOrganizationState();

  const getTriggerElement = (loading = false) => {
    if (showAsLabel) {
      return (
        <Button type="text" loading={loading} {...restProps}>
          {projectSite?.url}
        </Button>
      );
    }

    return (
      <Button
        type="link"
        className={styles.openWebsite}
        icon={<ExternalLink className={styles.websiteIcon} />}
        title={strings.openWebsite}
        loading={loading}
        {...restProps}
      >
        {!isMobileView && strings.openWebsite}
      </Button>
    );
  };

  return (
    <ProxyCompatibilityChecker
      getTriggerElement={getTriggerElement}
      projectSiteId={projectSite?.id}
      projectSiteUrl={projectSite?.url}
      proxyUrl={`https://${project.apiKey}_${projectSite?.id}.${proxyHost}`}
      project={project}
      showProxyPreview={hasProxyEnabledExperiment}
    />
  );
};

export const OpenWebsiteWithProxy: FC<Props> = ({
  project,
  projectSites,
  isMobileView,
  isReviewApp,
  apiKey,
  isAdminUser
}) => {
  const items: MenuProps['items'] = projectSites.map(site => ({
    key: site.id,
    label: (
      <OpenWebsiteButton
        project={project}
        projectSite={site}
        showAsLabel
        isMobileView={isMobileView}
      />
    )
  }));

  if (isReviewApp || isAdminUser) {
    items.push({
      label: (
        <Button
          type="text"
          onClick={() => window.open(`/test/widget?apikey=${apiKey}`, '_blank')}
        >
          Test Project Sidebar
        </Button>
      ),
      key: 'test'
    });
  }

  if (projectSites.length > 1 || isAdminUser) {
    return (
      <Dropdown
        className={styles.sitesDropdown}
        placement="bottomRight"
        menu={{ items }}
      >
        <Button
          type="link"
          className={styles.openWebsite}
          icon={<ExternalLink className={styles.websiteIcon} />}
          title={strings.openWebsite}
        >
          {!isMobileView && strings.openWebsite}
        </Button>
      </Dropdown>
    );
  } else if (projectSites.length === 1) {
    return (
      <OpenWebsiteButton
        projectSite={projectSites[0]}
        project={project}
        isMobileView={isMobileView}
      />
    );
  }
  return null;
};
